<template>
  <div v-if="visible" class="shift-close-overlay" role="presentation">
    <section
      ref="dialog"
      class="shift-close-dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby="shift-close-title"
      tabindex="-1"
      @keydown.esc="cancel"
    >
      <header class="shift-close-header">
        <span class="shift-close-icon"><i class="fas fa-lock"></i></span>
        <div class="shift-close-heading">
          <h2 id="shift-close-title">
            {{ $t('retailSale.shiftClose.title') }}
          </h2>
          <p>{{ $t('retailSale.shiftClose.subtitle') }}</p>
        </div>
        <button
          type="button"
          class="shift-close-dismiss"
          :aria-label="$t('no')"
          :disabled="loading"
          @click="cancel"
        >
          <i class="fas fa-times"></i>
        </button>
      </header>
      <div class="shift-close-content">
        <PageLoadingMask :loading="loadingSummary || loading" contained />
        <div v-if="summary" class="shift-close-summary">
          <div class="shift-close-period">
            <span>{{ $t('retailSale.shiftClose.period') }}</span>
            <strong
              >{{ formatDate(summary.opened_at) }} —
              {{ formatDate(summary.closed_at) }}</strong
            >
          </div>
          <div class="shift-close-cards">
            <div class="shift-close-card">
              <span>{{ $t('retailSale.shiftClose.invoices') }}</span>
              <strong>{{ summary.sales.invoice_count }}</strong>
            </div>
            <div class="shift-close-card">
              <span>{{ $t('retailSale.shiftClose.grandTotal') }}</span>
              <strong
                >{{ format(summary.sales.grand_total, currencyCode) }}
                {{ currencyCode }}</strong
              >
            </div>
          </div>
          <div class="shift-close-section-heading">
            <div class="shift-close-section-title">
              {{ $t('retailSale.shiftClose.paymentBreakdown') }}
            </div>
            <span class="shift-close-section-total">
              {{ format(summary.payments.base_total, currencyCode) }} {{ currencyCode }}
            </span>
          </div>
          <div v-if="summary.payments.methods.length">
            <div
              v-for="(payment, index) in summary.payments.methods"
              :key="`${payment.payment_method}-${payment.currency_code}`"
              class="shift-close-meta"
              :class="{ 'is-last': index === summary.payments.methods.length - 1 }"
            >
              <span
                >{{ payment.payment_method }} ({{
                  payment.currency_code
                }})</span
              >
              <strong>{{ format(payment.input_amount, payment.currency_code) }}</strong>
            </div>
          </div>
          <div v-else class="shift-close-empty">
            {{ $t('retailSale.shiftClose.noPayments') }}
          </div>
          <div class="shift-close-section-heading">
            <div class="shift-close-section-title">
              {{ $t('retailSale.shiftClose.changeReturned') }}
            </div>
          </div>
          <div
            v-for="(change, index) in changeReturned"
            :key="change.currency"
            class="shift-close-meta"
            :class="{ 'is-last': index === changeReturned.length - 1 }"
          >
            <span>{{ change.currency }}</span>
            <strong>{{ format(change.amount, change.currency) }}</strong>
          </div>
          <p class="shift-close-help">
            {{ $t('retailSale.shiftClose.changeReturnedHelp') }}
          </p>
          <div class="shift-close-section-heading">
            <div class="shift-close-section-title">
              {{ $t('retailSale.shiftClose.cashCounted') }}
            </div>
            <span class="shift-close-section-total">{{
              cashCurrencyCount
            }}</span>
          </div>
          <p class="shift-close-help">
            {{ $t('retailSale.shiftClose.cashHelp') }}
          </p>
          <div
            v-for="currency in cashCurrencies"
            :key="currency"
            class="shift-close-input-row"
          >
            <div>
              <strong>{{ currency }}</strong>
              <small>
                {{ $t('retailSale.shiftClose.netMovement') }}:
                {{ format(cashMovement(currency), currency) }}
              </small>
            </div>
            <div class="shift-close-counted-field">
              <div class="shift-close-counted">
                <span>{{ $t('retailSale.shiftClose.counted') }}</span>
                <input
                  v-model.number="cashCounted[currency]"
                  type="number"
                  min="0"
                  :step="cashCountStep(currency)"
                  :placeholder="$t('retailSale.shiftClose.counted')"
                  :aria-invalid="cashCountTooLow(currency)"
                  class="shift-close-input"
                  :class="{ 'is-invalid': cashCountTooLow(currency) }"
                />
              </div>
              <small v-if="cashCountTooLow(currency)" class="shift-close-count-error" role="alert">
                {{ $t('retailSale.shiftClose.cashCountTooLow', { amount: format(cashMovement(currency), currency), currency }) }}
              </small>
            </div>
          </div>
          <div v-if="requiresPassword" class="shift-close-password">
            <div>
              <strong>{{
                $t('retailSale.shiftClose.passwordRequired')
              }}</strong>
              <small>{{ $t('retailSale.shiftClose.passwordHelp') }}</small>
            </div>
            <input
              v-model="password"
              type="password"
              :placeholder="$t('retailSale.shiftClose.password')"
              class="shift-close-password-input"
            />
          </div>
        </div>
      </div>
      <footer class="shift-close-footer">
        <button
          type="button"
          class="shift-close-cancel"
          :disabled="loading"
          @click="cancel"
        >
          {{ $t('no') }}
        </button>
        <button
          type="button"
          class="shift-close-confirm"
          :disabled="!canSubmit || loading"
          @click="closeShift"
        >
          <i class="fas fa-lock"></i>
          {{ $t('retailSale.shiftClose.confirm') }}
        </button>
      </footer>
    </section>
  </div>
</template>

<script>
import {
  getCurrentShiftSummary,
  closeRetailSaleShift
} from '../../services/retail-sale-shift.service'
import PageLoadingMask from '../shared/PageLoadingMask.vue'
import { formatCurrencyAmount } from '../../utils/currency'

export default {
  name: 'RetailSaleShiftCloseModal',
  components: { PageLoadingMask },
  props: {
    visible: { type: Boolean, default: false },
    currencyCode: { type: String, default: '' },
    currencies: { type: Array, default: () => [] },
    requiresPassword: { type: Boolean, default: false }
  },
  data() {
    return {
      loadingSummary: false,
      loading: false,
      summary: null,
      password: '',
      cashCounted: {}
    }
  },
  computed: {
    changeReturned() {
      const returned = (this.summary && this.summary.payments.change_returned) || {}
      return ['USD', 'KHR'].map(currency => ({
        currency,
        amount: Number(returned[currency] || 0)
      }))
    },
    cashCurrencies() {
      return (this.summary && this.summary.payments.cash_currencies) || []
    },
    cashCurrencyCount() {
      return this.cashCurrencies.length
    },
    canSubmit() {
      return Boolean(
        this.summary &&
          !this.loadingSummary &&
          (!this.requiresPassword || this.password.trim()) &&
          this.cashCurrencies.every(currency => {
            const value = this.cashCounted[currency]
            return value !== null && value !== '' &&
              Number.isFinite(Number(value)) && Number(value) >= 0 &&
              !this.cashCountTooLow(currency)
          })
      )
    }
  },
  watch: {
    visible: {
      immediate: true,
      handler(value) {
        if (value) {
          this.loadSummary()
          this.$nextTick(() => this.$refs.dialog && this.$refs.dialog.focus())
        } else this.resetForm()
      }
    }
  },
  methods: {
    cancel() {
      if (!this.loading) this.$emit('cancel')
    },
    currencyConfig(currencyCode) {
      return this.currencies.find(currency => currency.currency_code === currencyCode) || {}
    },
    format(value, currencyCode) {
      return formatCurrencyAmount(value, this.currencyConfig(currencyCode))
    },
    cashCountStep(currencyCode) {
      const rounding = this.currencyConfig(currencyCode).currency_rounding
      return Math.pow(10, -Math.max(Number(rounding == null ? 2 : rounding), 0))
    },
    formatDate(value) {
      return value ? new Date(value).toLocaleString() : '-'
    },
    resetForm() {
      this.summary = null
      this.password = ''
      this.cashCounted = {}
    },
    async loadSummary() {
      this.loadingSummary = true
      this.password = ''
      try {
        const response = await getCurrentShiftSummary()
        this.summary = response.data || response
        this.cashCounted = {}
        this.cashCurrencies.forEach(currency => {
          this.$set(this.cashCounted, currency, null)
        })
      } catch (error) {
        this.$emit('error', error)
      } finally {
        this.loadingSummary = false
      }
    },
    cashMovement(currency) {
      return Number(
        (this.summary && this.summary.payments.cash_by_currency[currency]) || 0
      )
    },
    cashCountTooLow(currency) {
      const value = this.cashCounted[currency]
      return value !== null && value !== '' && Number.isFinite(Number(value)) &&
        Number(value) < this.cashMovement(currency)
    },
    async closeShift() {
      if (!this.canSubmit) return
      this.loading = true
      try {
        const response = await closeRetailSaleShift({
          opened_at: this.summary.opened_at,
          cash_counted: this.cashCounted,
          password: this.password
        })
        this.$emit('closed', response.data || response)
      } catch (error) {
        this.$emit('error', error)
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.shift-close-overlay {
  position: fixed;
  inset: 0;
  z-index: 1024;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 18px;
  background: rgba(15, 23, 42, 0.58);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
}

.shift-close-dialog {
  display: flex;
  flex-direction: column;
  width: min(100%, 620px);
  max-height: calc(100vh - 36px);
  overflow: hidden;
  border: 1px solid #dbe5f3;
  border-radius: 10px;
  background: #fff;
  box-shadow: 0 24px 70px rgba(15, 23, 42, 0.3);
  outline: none;
}

.shift-close-dialog button,
.shift-close-dialog button:focus {
  outline: none;
}

.shift-close-header,
.shift-close-footer {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
}

.shift-close-header {
  border-bottom: 1px solid #e2e8f0;
}

.shift-close-icon {
  display: grid;
  flex: 0 0 38px;
  place-items: center;
  width: 38px;
  height: 38px;
  border-radius: 8px;
  background: #eaf2ff;
  color: #0056a9;
}

.shift-close-heading {
  min-width: 0;
  flex: 1;
}

.shift-close-heading h2 {
  margin: 0;
  color: #0f172a;
  font-size: 18px;
  line-height: 1.2;
}

.shift-close-heading p {
  margin: 4px 0 0;
  color: #64748b;
  font-size: 12px;
}

.shift-close-dismiss {
  border: 0;
  background: transparent;
  color: #64748b;
  cursor: pointer;
  font-size: 16px;
}

.shift-close-content {
  position: relative;
  overflow-y: auto;
  min-height: 160px;
  padding: 18px 20px;
}

.shift-close-summary {
  color: #172033;
}

.shift-close-period {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 14px;
  padding: 10px 12px;
  border: 1px solid #dbe5f3;
  border-radius: 8px;
  background: #f5f8fd;
  color: #64748b;
  font-size: 12px;
}

.shift-close-period strong {
  color: #1e3a8a;
  font-size: 12px;
  text-align: right;
}

.shift-close-cards {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

.shift-close-card {
  padding: 14px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.04);
}

.shift-close-card span,
.shift-close-input-row small {
  display: block;
  color: #6b7280;
  font-size: 12px;
}

.shift-close-card strong {
  display: block;
  margin-top: 4px;
  font-size: 18px;
}

.shift-close-meta,
.shift-close-input-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 9px 0;
  border-bottom: 1px solid #f1f5f9;
}

.shift-close-meta.is-last {
  border-bottom: 0;
}

.shift-close-input-row {
  display: grid;
  grid-template-columns: minmax(90px, 1fr) 205px;
  align-items: center;
}

.shift-close-counted {
  display: flex;
  align-items: center;
  min-width: 0;
  gap: 8px;
  color: #64748b;
  font-size: 12px;
}

.shift-close-counted-field {
  min-width: 0;
}

.shift-close-count-error {
  display: block;
  margin-top: 4px;
  color: #b91c1c;
  text-align: right;
}

.shift-close-input.is-invalid {
  border-color: #b91c1c;
}

.shift-close-help {
  margin: 0 0 6px;
  color: #6b7280;
  font-size: 12px;
}

.shift-close-variance {
  white-space: nowrap;
  text-align: right;
  font-size: 12px;
  font-weight: 700;
}

.shift-close-variance.is-balanced {
  color: #15803d;
}

.shift-close-variance.is-over {
  color: #0369a1;
}

.shift-close-variance.is-short {
  color: #b91c1c;
}

.shift-close-section-title {
  font-weight: 700;
}

.shift-close-section-heading {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px solid #e5e7eb;
}

.shift-close-section-total {
  color: #64748b;
  font-size: 12px;
}

.shift-close-empty {
  padding: 12px 0;
  color: #94a3b8;
  font-size: 12px;
  font-style: italic;
}

.shift-close-input {
  width: 100%;
  min-width: 0;
  flex: 1;
  height: 36px;
  padding: 0 10px;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  background: #fff;
  color: #0f172a;
  font: inherit;
  text-align: right;
}

.shift-close-input:focus,
.shift-close-password-input:focus {
  border-color: #0056a9;
  outline: 2px solid rgba(0, 86, 169, 0.15);
}

.shift-close-password {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 220px;
  align-items: center;
  gap: 16px;
  margin-top: 18px;
  padding: 12px;
  border: 1px solid #fcd34d;
  border-radius: 8px;
  background: #fffbeb;
}

.shift-close-password strong,
.shift-close-password small {
  display: block;
}

.shift-close-password small {
  margin-top: 3px;
  color: #92400e;
  font-size: 11px;
}

.shift-close-password-input {
  width: 100%;
  height: 36px;
  padding: 0 10px;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  background: #fff;
  color: #0f172a;
  font: inherit;
}

.shift-close-footer {
  justify-content: flex-end;
  border-top: 1px solid #e2e8f0;
  background: #f8fafc;
}

.shift-close-cancel,
.shift-close-confirm {
  min-height: 36px;
  padding: 0 16px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 700;
}

.shift-close-cancel {
  border: 1px solid #cbd5e1;
  background: #fff;
  color: #334155;
}

.shift-close-confirm {
  border: 1px solid #0056a9;
  background: #0056a9;
  color: #fff;
}

.shift-close-confirm i {
  margin-right: 6px;
}

.shift-close-cancel:disabled,
.shift-close-confirm:disabled,
.shift-close-dismiss:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}

@media (max-width: 640px) {
  .shift-close-period,
  .shift-close-input-row,
  .shift-close-password {
    grid-template-columns: 1fr;
  }

  .shift-close-period {
    display: block;
  }

  .shift-close-period strong {
    display: block;
    margin-top: 4px;
    text-align: left;
  }

  .shift-close-counted,
  .shift-close-input {
    width: 100%;
  }

  .shift-close-variance {
    text-align: left;
  }

  .shift-close-header,
  .shift-close-footer {
    padding: 14px;
  }

  .shift-close-content {
    padding: 14px;
  }
}
</style>
